
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import moment from "moment";
import { TicketDto } from "../../dtos/ticketDto";
import DatePicker from "../../components/DatePicker.vue";
import { BlexonResolutionType } from "../../dtos/blexonResolutionType";
import { TransportProviderDto } from "../../dtos/transportProviderDto";
import rules from "@/store/rules";
import { TransportCalculationDto } from "@/dtos/transportCalculationDto";

@Component({
	components: {
		"date-picker": DatePicker,
	},
})
export default class TransportSettings extends Vue {
	@Prop()
	private ticketDto!: TicketDto;

	private transportCostCalculating: boolean = false;
	private transportCostCalculationMessage: string = "";
	private validForm: boolean = true;

	private transportProviders: TransportProviderDto[] = new Array();
	private transportCalculation: TransportCalculationDto | null = null;

	private requiredInteger0To100Rule = rules.requiredInteger0To100Rule;
	private requiredNonNegativeNumberRule = rules.requiredNonNegativeNumberRule;

	private async mounted() {
		await this.loadTransportProviders();
		await this.loadTransportSettings();
		if (this.transportCalculation && this.transportCalculation.transportProviderId == null) {
			await this.calculateTransportCost();
		}
	}

	private get replacementTransportProviders() {
		return this.transportProviders.filter(
			(t) => t.weightLimit > this.totalWeightOfReplacementParts
		);
	}

	private async loadTransportProviders() {
		const response = await httpClient().get(
			`Ticket/GetTransportProviders?ticketId=${this.ticketDto.id}`
		);
		this.transportProviders = response.data;
		this.transportProviders.push(
			{
				name: "Rampenabholung",
				id: 0,
				weightLimit: 2000000,
			},
			{
				name: "Nach Aufwand",
				id: -1,
				weightLimit: 2000000,
			}
		);
	}

	private async loadTransportSettings() {
		try {
			this.transportCostCalculating = true;
			const response = await httpClient().get(
				`Ticket/GetTransportSettings?ticketId=${this.ticketDto.id}`
			);
			this.transportCalculation = response.data;
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private async updateTransportSettings() {
		try {
			this.transportCostCalculating = true;
			await httpClient().post(
				`Ticket/UpdateTransportSettings?ticketId=${this.ticketDto.id}`,
				this.transportCalculation
			);
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private get totalWeightOfReplacementParts() {
		const relevantComplaints = this.ticketDto.complains.filter(
			(c) =>
				c.blexonResolution == BlexonResolutionType.AcceptedNacharbeit ||
				c.blexonResolution == BlexonResolutionType.AcceptedErsatzlieferung
		);
		let totalWeight: number = 0;
		relevantComplaints.forEach((complain) => {
			totalWeight += complain.part.gewicht * complain.numberOfWrongParts;
		});
		return totalWeight;
	}

	private get totalWeightOfNacharbeitParts() {
		const relevantComplaints = this.ticketDto.complains.filter(
			(c) => c.blexonResolution == BlexonResolutionType.AcceptedNacharbeit
		);
		let totalWeight: number = 0;
		relevantComplaints.forEach((element) => {
			totalWeight += element.part.gewicht * element.part.menge;
		});
		return totalWeight;
	}

	/**
	 * Calculates the total cost to send all part with Nacharbeit and with replacement parts
	 * to the customer
	 */
	private async calculateTransportCost() {
		this.transportCostCalculating = true;
		this.transportCostCalculationMessage = "";
		try {
			const response = await httpClient().post(
				`Ticket/CalculateDelivery?ticketId=${this.ticketDto.id}`
			);
			this.transportCalculation = response.data;
		} catch {
			this.transportCostCalculationMessage = "Transportkosten konnten nicht ermittelt werden";
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private get dueDate() {
		var date = new Date();
		if (this.transportCalculation != null) {
			date = this.transportCalculation.dueDate;
		}
		return moment(date).format("YYYY-MM-DD");
	}
}
